import { useEffect, useRef, useState } from 'react';
import { isNullish } from '@sas/nova-core/typeguards';
import { Search } from '@sas/react-svg';
import cx from 'classnames';
import { createUseTextLocalizer } from '../../locale/createUseTextLocalizer';
import { renderInlineCssVars, useDesignTokens } from '../../theme';
import { setRef } from '../../util/ref-util';
import { postfixTestId } from '../../util/testId';
import { Button, ButtonVariant } from '../Button';
import { useDensity } from '../Density';
import { useDirectSpeech } from '../DirectSpeech/DirectSpeechProvider';
import { Icon } from '../Icon';
import { BaseInput } from '../Input/__internal__/BaseInput';
import bundle from './NovaComponents-SearchField-gui-icu.properties';
import './SearchField.css';
import cn from './SearchField.css.json';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const useTextLocalizer = createUseTextLocalizer(bundle);
export const SearchField = props => {
  const [isActive, setIsActive] = useState(false);
  const [searchButtonIsActive, setSearchButtonIsActive] = useState(false);
  const dt = useDesignTokens();
  const {
    density
  } = useDensity();
  const tokens = {
    'bd-width': `0 0 ${dt.tokens['size-border-width-s']} 0`,
    bd: dt.tokens['color-action-border-bottom-control-normal'],
    bg: dt.tokens['color-action-background-control-normal'],
    fg: dt.tokens['color-action-text-control-normal'],
    'hover-bd': dt.tokens['color-action-border-bottom-control-hover'],
    'hover-bg': dt.tokens['color-action-background-control-hover'],
    'hover-fg': dt.tokens['color-action-text-control-placeholder'],
    'active-bd': dt.tokens['color-action-border-bottom-control-selected'],
    'active-bg': dt.tokens['color-action-background-control-selected'],
    'active-fg': dt.tokens['color-action-text-control-selected'],
    'disabled-fg': dt.tokens['color-action-text-control-disabled'],
    'disabled-bd': dt.tokens['color-action-border-control-disabled'],
    'disabled-bg': dt.tokens['color-action-background-control-disabled'],
    'spacing-xs': dt.tokens['space-base-xs'],
    'bottom-bd-anim-dur': dt.tokens['duration-300'],
    'bottom-bd-anim-tm-ease-in': dt.tokens['ease-in'],
    'bottom-bd-anim-tm-ease-out': dt.tokens['ease-out']
  };
  const disableEmptySearch = props.disableEmptySearch ?? false;
  const disableRole = props.disableRole ?? false;
  const style = {
    ...renderInlineCssVars(tokens, 'searchfield'),
    ...renderInlineCssVars({
      outline: 'var(--focus-light-bg)',
      'outline-offset': 'calc(-1 * var(--focus-width))'
    }, 'searchfield-focus')
  };
  const localize = useTextLocalizer();
  const inputRef = useRef(null);
  const clearButtonRef = useRef(null);
  const searchButtonRef = useRef(null);
  const shouldSearchAnnounce = useRef(false); // used for search mode, not filter mode

  let narratorMessage = '';
  const numberOfResults = props.numberOfResults;
  if (!isNullish(numberOfResults)) {
    if (numberOfResults === 0) {
      narratorMessage = localize('narrator.noresults.msg');
    } else if (numberOfResults === 1) {
      narratorMessage = localize('narrator.result.msg');
    } else {
      narratorMessage = localize(props.numberOfResultsIndeterminant ? 'narrator.results.indeterminant.msg' : 'narrator.results.msg', {
        count: numberOfResults.toString()
      });
    }
  }
  const directSpeech = useDirectSpeech();
  useEffect(() => {
    // Should only announce for the following conditions when there are numberOfResults:
    // 1.  Always announce in filter mode when search field has focus
    // 2.  When search mode, only announce when empty search is enabled and there is no value,
    //     or when there is a value in the search field and the search button is pressed
    const shouldAnnounce = (props.filterMode || !disableEmptySearch && props.value === '' || props.value !== '') && !isNullish(numberOfResults);
    if (shouldAnnounce) {
      if (props.filterMode && isActive) {
        // Always announce in filterMode when focused
        directSpeech?.notify(narratorMessage);
      } else if (shouldSearchAnnounce.current && (searchButtonIsActive || isActive)) {
        // When search mode, only announce if search event has fired
        directSpeech?.notify(narratorMessage);
        shouldSearchAnnounce.current = false;
      }
    }
  }, [directSpeech, isActive, narratorMessage, numberOfResults, props.filterMode, props.value, shouldSearchAnnounce, disableEmptySearch, searchButtonIsActive]);
  useEffect(() => {
    const handleBlur = () => {
      shouldSearchAnnounce.current = false;
      setSearchButtonIsActive(false);
    };
    const handleInputKeyDown = e => {
      if (e.key === 'Escape') {
        if (input?.value && input.value.length > 0) {
          props.onChange({
            value: ''
          });
          e.stopPropagation();
        }
      }
    };
    const searchButton = searchButtonRef.current;
    searchButton?.addEventListener('blur', handleBlur);
    const input = inputRef.current;
    input?.addEventListener('keydown', handleInputKeyDown);
    return () => {
      searchButton?.removeEventListener('blur', handleBlur);
      input?.removeEventListener('keydown', handleInputKeyDown);
    };
  });
  const getLandmarkLabel = () => {
    if (!props.filterMode) {
      return props.landmarkLabel ?? localize('searchField.landmark.search.label');
    }
  };
  return /*#__PURE__*/_jsxs("div", {
    "data-testid": props.testId,
    className: cx(props.className, cn.form, props.disabled && cn.disabled, isActive && cn.active),
    style: {
      ...style,
      ...props.style
    },
    role: disableRole || props.filterMode ? undefined : 'search',
    "data-landmark-label": !disableRole ? getLandmarkLabel() : undefined,
    onFocus: event => {
      if (document.activeElement === clearButtonRef.current || document.activeElement === searchButtonRef.current) {
        setIsActive(false);
      }
      if (document.activeElement === inputRef.current) {
        setIsActive(true);
      }
      if (event.relatedTarget !== inputRef.current && event.relatedTarget !== searchButtonRef.current && (clearButtonRef.current === null || event.relatedTarget !== clearButtonRef.current)) {
        props.onFocus?.();
      }
    },
    onBlur: event => {
      if (event.relatedTarget !== inputRef.current && event.relatedTarget !== searchButtonRef.current && (clearButtonRef.current === null || event.relatedTarget !== clearButtonRef.current)) {
        props.onBlur?.();
      }
    },
    children: [props.filterMode ? /*#__PURE__*/_jsx("div", {
      className: cn.searchButtonContainer,
      children: /*#__PURE__*/_jsx(Icon, {
        className: cn.searchButton,
        icon: Search,
        tooltip: "",
        ariaLabel: "",
        ariaHidden: true,
        testId: postfixTestId(props.testId, 'filterIcon'),
        theme: () => ({
          '--icon-default-override-fg': props.disabled ? dt.tokens['color-action-text-standard-disabled'] : dt.tokens['color-action-text-standard-normal']
        })
      })
    }) : null, /*#__PURE__*/_jsx(BaseInput, {
      ref: element => {
        inputRef.current = element;
        setRef(props.inputRef, element);
      },
      clearButtonRef: clearButtonRef,
      hideBorder: true,
      style: {
        '--input-hover-bg': 'transparent',
        '--input-focus-bg': 'transparent',
        // match height with other input field when using density high
        ...(density === 'high' && {
          '--input-min-height': 'calc(1.5rem - 2px)'
        })
      },
      placeholder: props.placeholder ?? (props.filterMode ? localize('searchField.placeholder.filter.label') : localize('searchField.placeholder.search.label')),
      className: cn.searchInput,
      onChange: e => {
        props.onChange({
          value: e.value
        });
        if (props.filterMode) {
          props.onCommit?.({
            value: e.value
          });
          return;
        }
        if (!disableEmptySearch) {
          if (!e.value || e.value === '') {
            props.onCommit?.({
              value: e.value
            });
            shouldSearchAnnounce.current = true;
          }
        }
        if (e.value === '') {
          setTimeout(() => inputRef.current?.focus(), 0);
        }
        if (!props.filterMode && shouldSearchAnnounce.current) {
          shouldSearchAnnounce.current = false;
        }
      },
      onCommit: e => {
        if (props.disabled) return;
        if (e.reason === 'enter') {
          props.onCommit?.({
            value: e.value
          });
          shouldSearchAnnounce.current = true;
        }
      },
      value: props.value,
      disabled: props.disabled,
      onBlur: () => {
        setIsActive(false);
        if (shouldSearchAnnounce.current && props.filterMode) {
          shouldSearchAnnounce.current = false;
        }
      },
      onFocus: () => {
        setIsActive(true);
      },
      ariaLabel: props.ariaLabel ?? (props.filterMode ? localize('searchField.input.filter.aria') : localize('searchField.input.search.aria')),
      maxLength: props.maxLength,
      testId: postfixTestId(props.testId, 'searchInput'),
      commitOnEnterInDialog: props.commitOnEnterInDialog ?? true,
      showClearButton: props.value !== '',
      clearButtonTooltip: props.clearButtonTooltip ?? (props.filterMode ? localize('searchField.clearButton.filter.tooltip.label') : localize('searchField.clearButton.search.tooltip.label')),
      clearButtonAriaLabel: props.clearButtonAriaLabel ?? (props.filterMode ? localize('searchField.clearButton.filter.aria') : localize('searchField.clearButton.search.aria')),
      clearButtonTokens: () => ({
        fg: props.disabled ? dt.tokens['color-action-text-standard-disabled'] : dt.tokens['color-action-text-control-normal'],
        filter: 'none',
        bg: 'transparent'
      })
    }), props.filterMode ? null : /*#__PURE__*/_jsx(Button, {
      ref: searchButtonRef,
      variant: ButtonVariant.Icon,
      className: cn.searchButton,
      disabled: props.disabled,
      icon: {
        icon: Search
      },
      tooltip: props.searchButtonTooltip ?? localize('searchField.searchButton.startSearch.tooltip.label'),
      ariaLabel: props.searchButtonAriaLabel ?? localize('searchField.searchButton.startSearch.aria'),
      onPress: () => {
        props.onCommit?.({
          value: props.value
        });
        shouldSearchAnnounce.current = true;
        setSearchButtonIsActive(true);
      },
      testId: postfixTestId(props.testId, 'searchButton'),
      theme: () => ({
        '--button-fg': props.disabled ? dt.tokens['color-action-text-standard-disabled'] : dt.tokens['color-action-text-standard-normal'],
        '--button-filter': 'none',
        '--button-bg': 'transparent',
        '--button-hover-bd': 'transparent',
        '--button-hover-fg': props.disabled ? dt.tokens['color-action-text-standard-disabled'] : dt.tokens['color-action-text-standard-normal'],
        '--button-hover-filter': 'none',
        '--button-hover-bg': 'transparent',
        '--button-active-fg': props.disabled ? dt.tokens['color-action-text-standard-disabled'] : dt.tokens['color-action-text-standard-normal'],
        '--button-active-filter': 'none',
        '--button-active-bg': 'transparent',
        '--button-active-bd': 'transparent'
      })
    })]
  });
};
//# sourceMappingURL=SearchField.js.map